import { type AxiosResponse } from 'axios';
import axios from 'axios';

import { del, get, post, put } from '@utilities';
import { API_URL, TOKEN_KEY } from '@utilities/constants';
import { http } from '@utilities/api';

export const fetchAllCategories = async (data: Record<string, any>): Promise<AxiosResponse> => await post(`product/category/all`, data);
export const addCategorySubCategory = async (body: Record<string, any>): Promise<AxiosResponse> => await post(`product/category`, body);
export const updateCategorySubCategory = async (body: Record<string, any>): Promise<AxiosResponse> =>
	await post(`product/sub_category`, body);
export const deleteCategory = async (categoryId): Promise<AxiosResponse> =>
	await put(`product/category/${categoryId}/delete`);
export const deleteSubCategory = async (body: Record<string, any>): Promise<AxiosResponse> =>
	await put(`product/sub_category/delete`, body);
export const fetchAllCategoriesDropdown = async (): Promise<AxiosResponse> => await get(`product/category/drop_down`);
export const fetchAllSubCategoriesDropdown = async (categoryId: any): Promise<AxiosResponse> =>
	await get(`product/sub_category/drop_down?category_id=${categoryId}`);
/** Sub-category → default GLB URL (3D quotation fallback when product has no model URL) */
export const fetchDefaultSubcategoryModels = async (): Promise<AxiosResponse> =>
	await get(`product/sub_category/default-models`);
export const deleteProduct = async (body): Promise<AxiosResponse> => await put(`product/item/delete`, body);
export const fetchProductDetails = async (itemId): Promise<AxiosResponse> => await get(`product/item/${itemId}`);
export const fetchProductItemHistory = async (itemId, limit = 50): Promise<AxiosResponse> =>
	await http.get(`product/item/${itemId}/history`, { params: { limit } });
export const fetchNextProductCode = async (categoryId: number, subCategoryId: number): Promise<AxiosResponse> =>
	await get(`product/item/next_code?category_id=${categoryId}&sub_category_id=${subCategoryId}`);
export const fetchAllProductWarranty = async (data): Promise<AxiosResponse> => await post(`warranty/all`, data);
export const fetchAllProducts = async (data): Promise<AxiosResponse> => await post(`product/item/all`, data);
export const fetchAllProductItemsDropdown = async (data): Promise<AxiosResponse> =>
	await get('product/item/drop_down', data);
export const fetchAllProductItemsDropdownV2 = async (area): Promise<AxiosResponse> =>
	await get(`product/item/drop_down_v2?area=${area}`);
// export const fetchAllProductItemsDropdown = async (subCategoryId?): Promise<AxiosResponse> =>
// 	await get(subCategoryId ? `product/item/drop_down?sub_category_id=${subCategoryId}` : `product/item/drop_down`);
export const addWarranty = async (body: Record<string, any>): Promise<AxiosResponse> => await post(`warranty`, body);
export const updateWarranty = async (warrantyId, body: any): Promise<AxiosResponse> =>
	await put(`warranty/${warrantyId}`, body);
export const addProductItem = async (data): Promise<AxiosResponse> => await post(`product/item`, data);
export const activateDocument = async (data: Record<string, any>): Promise<AxiosResponse> => await post(`document/activate`, data);
export const deleteWarranty = async (body): Promise<AxiosResponse> => await put(`warranty/delete`, body);
export const bulkEditWarranty = async (body: { bulk: Array<Record<string, any>> }): Promise<AxiosResponse> =>
	await put(`warranty/bulk`, body);
export const updateProductItem = async (itemId, body: any): Promise<AxiosResponse> =>
	await put(`product/item/${itemId}`, body);

export const fetchParametricVariants = async (): Promise<AxiosResponse> =>
	await get('admin/parametric-variants');
export const fetchParametricVariantRules = async (ruleFileId: string, version = 1): Promise<AxiosResponse> =>
	await get(`builder/variant-rules/${encodeURIComponent(ruleFileId)}?version=${version}`);
export const fetchBuilderRules = async (builderCode: string): Promise<AxiosResponse> =>
	await get(`builder/rules/${encodeURIComponent(builderCode)}`);
export const createParametricVariant = async (body: Record<string, any>): Promise<AxiosResponse> =>
	await post('admin/parametric-variants', body);
export const updateParametricVariant = async (
	variantId: string,
	body: Record<string, any>
): Promise<AxiosResponse> => await put(`admin/parametric-variants/${encodeURIComponent(variantId)}`, body);
export const updateParametricVariantPreviewImage = async (
	variantId: string,
	version: number,
	body: Record<string, unknown>
): Promise<AxiosResponse> =>
	await put(`admin/parametric-variants/${encodeURIComponent(variantId)}/${version}/preview-image`, body);
export const publishParametricVariant = async (variantId: string, version: number): Promise<AxiosResponse> =>
	await post(`admin/parametric-variants/${encodeURIComponent(variantId)}/publish?version=${version}`, {});
export const archiveParametricVariant = async (variantId: string, version: number): Promise<AxiosResponse> =>
	await post(`admin/parametric-variants/${encodeURIComponent(variantId)}/archive?version=${version}`, {});
export const updateCoverImage = async (documentUrlId): Promise<AxiosResponse> =>
	await put(`document/cover_image/${documentUrlId}`, {});
export const fetchProductAreaListDropdown = async (): Promise<AxiosResponse> =>
	await get(`product/area_list/drop_down`);
export const fetchAreaListDropdown = async (quotationID): Promise<AxiosResponse> => {
	if (!quotationID || quotationID === 'undefined' || quotationID === 'null') {
		throw new Error('A quotation must be created before its areas can be loaded.');
	}
	return await get(`quotation/${quotationID}/area`, {});
};
export const updateProductDocuments = async (body: Record<string, any>): Promise<AxiosResponse> => await put(`document`, body);
export const deleteBookingDocuments = async (body: Record<string, any>): Promise<AxiosResponse> =>
	await put(`document/document_url/delete`, body);
export const normalizeBuilderPresetType = (builderType: unknown) => {
	const normalizedBuilderType = String(builderType || '')
		.toUpperCase()
		.trim();
	if (!normalizedBuilderType) return '';

	const cabinetPresetTypes = new Set([
		'KBC',
		'KWC',
		'CABINET',
		'CBBUILDER',
		'CBBUILDERBASE',
		'CBBUILDERBASEBCUNIT',
		'CBBUILDERBASEBPO',
		'CBBUILDERBASEHOB',
		'CBBUILDERBASELCORNER',
		'CBBUILDERBASEPANTRY',
		'CBBUILDERBASESINKBCUNIT',
		'CBBUILDERBASESINKUNIT',
		'CBBUILDERFRAMELOFT',
		'CBBUILDERGTPTUNIT',
		'CBBUILDERTALLUNIT',
		'CBBUILDERWALLBCUNIT',
		'CBBUILDERWALLROUNIT',
		'CBWBFRLOFT'
	]);

	if (cabinetPresetTypes.has(normalizedBuilderType) || normalizedBuilderType.startsWith('CBBUILDER')) {
		return 'CABINET';
	}
	if (['WB', 'WARDROBE'].includes(normalizedBuilderType)) return 'WARDROBE';
	if (['CT', 'COUNTERTOP'].includes(normalizedBuilderType)) return 'COUNTERTOP';
	if (['WP', 'WALL_PANEL', 'WALL PANEL', 'WALLPANEL'].includes(normalizedBuilderType)) return 'WP';
	if (['WT', 'WALL_TILES', 'WALL TILES', 'WALLTILES'].includes(normalizedBuilderType)) return 'WT';
	if (['EB', 'ELECTRICAL'].includes(normalizedBuilderType)) return 'EB';
	if (['WM', 'MOULDING', 'WALL_MOULDING', 'WALL MOULDING'].includes(normalizedBuilderType)) return 'MOULDING';
	if (['WALLPAINTBUILDER', 'WALLPAINTV2BUILDER', 'WALL_PAINT', 'WALL PAINT', 'PAINT'].includes(normalizedBuilderType)) {
		return 'WALL_PAINT';
	}
	if (['CURTAIN', 'CURTAIN_BUILDER', 'CURTAINBUILDER'].includes(normalizedBuilderType)) return 'CURTAIN';
	if (['FC', 'FALSE_CEILING', 'FALSE CEILING'].includes(normalizedBuilderType)) return 'FC';
	return normalizedBuilderType;
};

export const fetchPresetKeys = async (builderType: unknown): Promise<AxiosResponse> =>
	await get(`builder/preset?type=${normalizeBuilderPresetType(builderType)}`);
export const fetchWarrantyDetailsByCodes = async (warrantyCodes: string[]): Promise<AxiosResponse> =>
	await post('warranty/code', { warranty_codes: warrantyCodes });
export const bulkEditProducts = async (body: { items: Array<Record<string, any>> }): Promise<AxiosResponse> =>
	await post('product/item/bulk', body);
export const fetchInternalCategories = async (): Promise<AxiosResponse> => await get('product/internal_category');
const taxonomyPost = async (endpoint: string, body: Record<string, any>): Promise<AxiosResponse> =>
	await new Promise((resolve, reject) => {
		axios
			.post(`${API_URL}/${endpoint}`, body, {
				headers: {
					Accept: 'application/json',
					Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}`,
					Vary: '*'
				}
			})
			.then(resolve)
			.catch(e => {
				reject(e?.response?.data);
			});
	});
export const fetchProductTaxonomy = async (): Promise<AxiosResponse> => {
	const [xorGroupsResponse, productGroupsResponse] = await Promise.all([
		get('product/taxonomy/xor-groups'),
		get('product/taxonomy/product-groups')
	]);
	return {
		...xorGroupsResponse,
		data: {
			xorGroups: xorGroupsResponse.data,
			productGroups: productGroupsResponse.data
		}
	};
};
export const addXorGroup = async (body: Record<string, any>): Promise<AxiosResponse> =>
	await taxonomyPost('product/taxonomy/xor-groups', body);
export const updateXorGroup = async (id: string, body: Record<string, any>): Promise<AxiosResponse> =>
	await put('product/taxonomy/xor-groups/' + encodeURIComponent(id), {
		...body,
		isActive: true
	});
export const deleteXorGroup = async (name: string): Promise<AxiosResponse> =>
	await put(`product/taxonomy/xor-groups/${encodeURIComponent(name)}/active?active=false`);
export const addProductGroup = async (body: Record<string, any>): Promise<AxiosResponse> =>
	await taxonomyPost('product/taxonomy/product-groups', body);
export const updateProductGroup = async (id: string, body: Record<string, any>): Promise<AxiosResponse> =>
	await put('product/taxonomy/product-groups/' + encodeURIComponent(id), {
		...body,
		isActive: true
	});
export const deleteProductGroup = async (name: string): Promise<AxiosResponse> =>
	await put(`product/taxonomy/product-groups/${encodeURIComponent(name)}/active?active=false`);

/** POST /document/upload - get upload URLs for bulk edit product images. Response has documents_upload_responses. */
export const uploadDocument = async (body: {
	reference_id: string;
	reference_type: string;
	documents: Array<{
		category: string;
		sub_category: string;
		attachments: Array<{ file: string; cover_image: boolean }>;
		document_id?: number;
	}>;
}): Promise<AxiosResponse> => await post('document/upload', body);
export const fetchDocumentsByIds = async (documentIds: number[]): Promise<AxiosResponse> =>
	await post('document/all', { documentIds });

/** DELETE /model-asset/{path} - delete a 3D model from S3. */
export const deleteModelAsset = async (assetPath: string): Promise<AxiosResponse> =>
	await del(`model-asset/${assetPath}`);

/** GET /product/sub_category/default-models - list all subcategories with default model info. */
export const fetchDefaultModels = async (): Promise<AxiosResponse> =>
	await get('product/sub_category/default-models');

/** PUT /product/sub_category/{id}/default-model - update subcategory default model path. */
export const updateSubCategoryDefaultModel = async (
	subCategoryId: number | string,
	body: Record<string, any>
): Promise<AxiosResponse> => await put(`product/sub_category/${subCategoryId}/default-model`, body);
